import React, { useEffect, useState } from "react";
import { useSwipeable } from "react-swipeable";
import { VersionType } from "../../services/types";
import { useGlobalContext } from "../context/AuthContext";
import Navigation from "../others/Navigation";
import TextareaWithLimit from "../others/TextareaWithLimit";
import Edit from "./Edit";

type VerionProps = {
  versions: VersionType[];
  gistLength: number;
  gistCurrentIndex: number;
  editCurrentIndex: number;
  isFocusMode: boolean;
  onEnterFocusMode: () => void;
  onExitFocusMode: () => void;
  setEditCurrentIndex: React.Dispatch<React.SetStateAction<number>>;
  versionCurrentIndex: number;
  setVersionCurrentIndex: React.Dispatch<React.SetStateAction<number>>;
  showComments?: boolean;
};

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  dateStyle: "medium",
});

const Version: React.FC<VerionProps> = ({
  versions,
  editCurrentIndex,
  gistLength,
  isFocusMode,
  onEnterFocusMode,
  onExitFocusMode,
  setEditCurrentIndex,
  versionCurrentIndex,
  setVersionCurrentIndex,
  gistCurrentIndex,
  showComments = true,
}) => {
  const [createVersion, setCreateVersion] = useState(false);
  const [dragOffset, setDragOffset] = useState<number>(0);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [newVersionData, setNewVersionData] = useState<string>(
    versions[versionCurrentIndex]?.point
  );

  const { setTextareaEdit, user, textareaEdit } = useGlobalContext();

  const handleNext = () => {
    setVersionCurrentIndex((prevIndex) => (prevIndex + 1) % versions.length);
    setEditCurrentIndex(0);
  };

  const handlePrev = () => {
    setVersionCurrentIndex((prevIndex) =>
      prevIndex === 0 ? versions.length - 1 : prevIndex - 1
    );
    setEditCurrentIndex(0);
  };

  const handleIndexChange = (newIndex: number) => {
    setVersionCurrentIndex(newIndex);
    setEditCurrentIndex(0);
  };

  const handleSwipe = useSwipeable({
    onSwiping: (event) => {
      setIsDragging(true);
      setDragOffset(event.deltaX);
    },
    onSwiped: (event) => {
      setIsDragging(false);
      if (event.deltaX > 100) {
        handlePrev();
      } else if (event.deltaX < -100) {
        handleNext();
      }
      setDragOffset(0);
    },
    onTap: () => {
      setIsDragging(false);
      setDragOffset(0);
    },
    // Mouse drag is disabled so users can select/copy text without moving the carousel.
    // Touch swipes still work on mobile; desktop navigation remains available through controls.
    trackMouse: false,
  });

  useEffect(() => {
    setVersionCurrentIndex(0);
  }, [gistCurrentIndex, setVersionCurrentIndex]);

  useEffect(() => {
    if (!createVersion) {
      setNewVersionData(versions[versionCurrentIndex].point);
    }
  }, [versions, versionCurrentIndex, createVersion]);

  if (!versions || versions.length === 0) {
    return (
      <div
        className="text-center py-8"
        style={{ color: "var(--color-gray-warm)" }}
      >
        No Versions available.
      </div>
    );
  }

  const activeVersion = versions[versionCurrentIndex];

  const versionCarouselStyle: React.CSSProperties = {
    transform: `translateX(${dragOffset}px)`,
    transition: isDragging ? "none" : "transform 0.3s ease-in-out",
    opacity: isDragging ? 0.96 : 1,
  };

  return (
    <div
      style={versionCarouselStyle}
      className={`gist-version-surface ${isFocusMode ? "is-focus-mode" : ""}`}
    >
      <div className="gist-summary-header">
        <div className="gist-person">
          <img
            src={activeVersion?.user?.image || "/profile.png"}
            className="gist-avatar"
            style={{ border: "2px solid var(--color-border)" }}
            alt="User avatar"
          />
          <div className="gist-person-copy">
            <div className="gist-person-name">{activeVersion?.user?.name}</div>
            <div className="gist-person-date">
              {activeVersion?.createdAt &&
                dateFormatter.format(Date.parse(activeVersion.createdAt))}
            </div>
          </div>
        </div>

        <div className="gist-level-controls">
          <div className="gist-level-chip">Version</div>
          <Navigation
            currentIndex={versionCurrentIndex}
            totalItems={versions.length}
            onChangeIndex={handleIndexChange}
            handlePrev={handlePrev}
            handleNext={handleNext}
          />
        </div>
      </div>

      <div className="gist-summary-zone" {...handleSwipe}>
        {textareaEdit ? (
          <TextareaWithLimit
            maxChars={300}
            text={newVersionData}
            setText={setNewVersionData}
          />
        ) : (
          <h2
            onDoubleClick={() => user && setTextareaEdit(true)}
            className="gist-summary-text"
            style={{ cursor: user ? "pointer" : "default" }}
          >
            {newVersionData}
          </h2>
        )}
      </div>


      {activeVersion.edits && (
        <Edit
          edits={activeVersion.edits}
          isFocusMode={isFocusMode}
          onEnterFocusMode={onEnterFocusMode}
          onExitFocusMode={onExitFocusMode}
          versionData={activeVersion}
          newVersionData={newVersionData}
          createVersion={createVersion}
          setCreateVersion={setCreateVersion}
          versionsLength={versions.length}
          versionCurrentIndex={versionCurrentIndex}
          setVersionCurrentIndex={setVersionCurrentIndex}
          editCurrentIndex={editCurrentIndex}
          setEditCurrentIndex={setEditCurrentIndex}
          gistCurrentIndex={gistCurrentIndex}
          gistLength={gistLength}
          showComments={showComments}
        />
      )}
    </div>
  );
};

export default Version;
